// Team line-ups submitted by captains.
//
// WHY A COLLECTION OF ITS OWN, and not a field on the tournament. Squads and line-ups
// live on the tournament document, and the obvious design would let a captain write them
// there. It cannot be secured: the security-rules language can compare whole values but
// cannot diff one entry of an array of maps, so "you may change your own team's line-up
// and nothing else" is not expressible. A captain granted update on the tournament could
// rewrite the pairings, the settings or another team's squad, and no rule could tell.
//
// So a captain writes ONE document that is theirs alone, and the arbiter's client reads
// them and applies them into the tournament — exactly the shape `registrations` already
// uses, for exactly the same reason. See the teamLineups block in firestore.rules.
//
// THE DOCUMENT ID IS PART OF THE CONTRACT: {tournamentId}_{teamId}_{round}, checked by the
// rules against the fields inside. That is what stops a captain keeping a second,
// competing line-up for the same round under an id of their choosing.

import {
    collection, doc, getDocs, setDoc, deleteDoc, query, where
} from "https://www.gstatic.com/firebasejs/12.12.1/firebase-firestore.js";
import { db, auth } from "./firebase-auth.js";

const COLLECTION = 'teamLineups';

export function lineupDocId(tournamentId, teamId, round) {
    return `${tournamentId}_${teamId}_${round}`;
}

/** The signed-in user's address, lower-cased — the form every rule compares against. */
function myEmail() {
    const u = auth && auth.currentUser;
    return u && u.email ? u.email.toLowerCase() : '';
}

/**
 * Submit (or replace) the line-up this captain will field next round.
 *
 * Rejected by the rules unless the caller's VERIFIED address is the one recorded for the
 * team and the round is still ahead of the tournament, so the failure path here is a real
 * message rather than a silent no-op.
 *
 * @param {Object} arg { tournamentId, teamId, round, lineup: [playerId|null] }
 */
export async function submitLineup({ tournamentId, teamId, round, lineup }) {
    const email = myEmail();
    // Reaches the captain as a dialog, so it goes through the translator — guarded the
    // same way TeamEngine.text is, for the environments where i18n is not loaded.
    if (!email) {
        throw new Error(
            (typeof window !== 'undefined' && window.TeamEngine)
                ? window.TeamEngine.text('You must be signed in to submit a line-up.')
                : 'You must be signed in to submit a line-up.'
        );
    }

    await setDoc(doc(db, COLLECTION, lineupDocId(tournamentId, teamId, round)), {
        tournamentId,
        teamId,
        round,
        captainEmail: email,
        // Empty boards are stored as '' rather than null: Firestore keeps nulls, but an
        // empty string survives the round trip through every reader here unchanged and
        // keeps the array a list of strings, which is what the rules type-check expects.
        lineup: (lineup || []).map(id => id || ''),
        submittedAt: Date.now()
    });
}

/**
 * Every line-up submitted for a tournament, as { [teamId]: { round, lineup, captainEmail } }
 * for one round.
 *
 * Readable by the tournament's arbiters (and by a captain for their own). The query filters
 * on tournamentId because the rules only permit a listing that does — an unfiltered scan of
 * the collection is denied.
 */
export async function fetchLineupsForRound(tournamentId, round) {
    const snap = await getDocs(query(
        collection(db, COLLECTION),
        where('tournamentId', '==', tournamentId)
    ));

    const out = {};
    snap.forEach(d => {
        const data = d.data();
        if (Number(data.round) !== Number(round)) return;
        out[data.teamId] = {
            id: d.id,
            round: Number(data.round),
            // Back to the app's own shape: an empty board is null everywhere else.
            lineup: (data.lineup || []).map(id => id || null),
            captainEmail: data.captainEmail || '',
            submittedAt: data.submittedAt || 0
        };
    });
    return out;
}

/**
 * Clear a submission once it has been applied.
 *
 * Arbiters only — a captain deleting their own would turn "this is my line-up" into "I
 * submitted nothing", which is a different statement.
 */
export async function clearLineup(tournamentId, teamId, round) {
    await deleteDoc(doc(db, COLLECTION, lineupDocId(tournamentId, teamId, round)));
}
